// src/sectors/pilates/sessions/sections/CustomerSection.jsx
import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@mui/material/Icon';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDInput from 'components/MDInput';

import { Section } from './Section';
import { membersApi } from 'sectors/pilates/members/api/membersApi';

function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function normalizeMember(m) {
  const fullName =
    m?.fullName ||
    `${m?.name || m?.firstName || ''} ${m?.surname || m?.lastName || ''}`.trim() ||
    (m?.id != null ? `#${m.id}` : '');

  const trainerId = m?.trainerId ?? m?.assignedTrainerId ?? m?.staffId ?? m?.coachId ?? null;
  const trainerName =
    m?.trainerName ?? m?.assignedTrainerName ?? m?.staffName ?? m?.coachName ?? null;

  return { ...m, fullName, trainerId, trainerName };
}

export function CustomerSection({
  sx,
  tokens,
  trainerId, // ✅ seçili hoca id (SessionCreatePackageModal'dan gelecek)
  selectedIds = [],
  setSelectedIds,
  onSelectedMembersChange,
}) {
  const theme = useMuiTheme();
  const surface = tokens?.surface || '#fff';
  const border = tokens?.border || theme.palette.divider;

  const [inputValue, setInputValue] = useState('');
  const q = useDebouncedValue(inputValue, 250);

  const [optionsRaw, setOptionsRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const tId = trainerId && trainerId !== 'ALL' ? String(trainerId) : '';

  // ✅ selected members (objeleri)
  const selectedMembers = useMemo(() => {
    const set = new Set((selectedIds || []).map(String));
    return (optionsRaw || []).filter((m) => set.has(String(m.id)));
  }, [optionsRaw, selectedIds]);

  // ✅ parent'a bildir
  useEffect(() => {
    onSelectedMembersChange?.(selectedMembers);
  }, [selectedMembers, onSelectedMembersChange]);

  // ✅ options fetch
  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setIsError(false);
      try {
        const res = await membersApi.list({ page: 0, size: 50, q });

        // ✅ axios / farklı shape destekle
        const data = res?.data ?? res;
        const arr = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(res?.items)
          ? res.items
          : [];

        if (!alive) return;

        const norm = arr.map(normalizeMember);
        setOptionsRaw(norm);
      } catch (e) {
        if (!alive) return;
        setOptionsRaw([]);
        setIsError(true);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [q]);

  // ✅ sıralama: önce seçili hocanın üyeleri + en üstte seçilmiş olanlar
  const options = useMemo(() => {
    const list = Array.isArray(optionsRaw) ? optionsRaw : [];
    const selectedSet = new Set((selectedIds || []).map(String));

    return list.slice().sort((a, b) => {
      // 1) seçili olanlar en üste
      const aSel = selectedSet.has(String(a?.id)) ? 0 : 1;
      const bSel = selectedSet.has(String(b?.id)) ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;

      // 2) seçili hocanın üyeleri üste
      const aMine = tId && String(a?.trainerId ?? '') === tId ? 0 : 1;
      const bMine = tId && String(b?.trainerId ?? '') === tId ? 0 : 1;
      if (aMine !== bMine) return aMine - bMine;

      // 3) alfabetik
      return String(a?.fullName || '').localeCompare(String(b?.fullName || ''), 'tr');
    });
  }, [optionsRaw, selectedIds, tId]);

  const addMember = (m) => {
    if (!m?.id) return;
    const id = String(m.id);
    setSelectedIds?.((prev) => {
      const arr = Array.isArray(prev) ? prev.map(String) : [];
      if (arr.includes(id)) return arr;
      return [...arr, id];
    });
  };

  const removeMember = (id) => {
    setSelectedIds?.((prev) =>
      Array.isArray(prev) ? prev.filter((x) => String(x) !== String(id)) : []
    );
  };

  const isMine = (m) => tId && String(m?.trainerId ?? '') === tId;

  return (
    <Section title="Katılımcılar" icon="groups" sx={sx}>
      <MDBox sx={{ display: 'grid', gap: 1 }}>
        <Autocomplete
          options={options}
          value={null}
          onChange={(e, val) => addMember(val)}
          inputValue={inputValue}
          onInputChange={(e, val) => setInputValue(val)}
          getOptionLabel={(opt) => opt?.fullName || ''}
          isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
          loading={loading}
          renderOption={(props, option) => {
            const mine = isMine(option);
            return (
              <li
                {...props}
                style={{
                  backgroundColor: mine ? alpha(theme.palette.info.main, 0.12) : 'transparent',
                  fontWeight: mine ? 800 : 600,
                }}
              >
                {option.fullName}
                {mine ? ' • (Eğitmenin Üyesi)' : ''}
              </li>
            );
          }}
          renderInput={(params) => (
            <MDInput
              {...params}
              label="Üye ekle / ara"
              sx={{
                ...tokens?.input,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  background: surface,
                },
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon fontSize="small">search</Icon>
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              helperText={isError ? 'Üyeler yüklenemedi' : 'Birden fazla üye ekleyebilirsin.'}
            />
          )}
        />

        {(selectedIds?.length || 0) > 0 ? (
          <MDBox
            sx={{
              borderRadius: '18px',
              border: `1px solid ${border}`,
              background: surface,
              p: 1,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.8,
              alignItems: 'center',
            }}
          >
            {selectedMembers.map((m) => {
              const mine = isMine(m);

              return (
                <Chip
                  key={String(m.id)}
                  label={m.fullName}
                  onDelete={() => removeMember(m.id)}
                  icon={<Icon sx={{ fontSize: 18 }}>person</Icon>}
                  sx={{
                    borderRadius: '999px',
                    fontWeight: 900,
                    background: mine ? alpha(theme.palette.info.main, 0.15) : surface,
                    border: `1.5px solid ${mine ? theme.palette.info.main : border}`,
                    color: mine ? theme.palette.info.main : theme.palette.text.primary,
                  }}
                />
              );
            })}

            {/* options'ta bulunamayan id’ler (ilk render’da) */}
            {selectedIds
              .filter((id) => !selectedMembers.some((m) => String(m.id) === String(id)))
              .map((id) => (
                <Chip
                  key={`ghost-${id}`}
                  label={`#${id}`}
                  onDelete={() => removeMember(id)}
                  sx={{
                    borderRadius: '999px',
                    fontWeight: 800,
                    border: `1px solid ${border}`,
                    background: surface,
                  }}
                />
              ))}
          </MDBox>
        ) : null}
      </MDBox>
    </Section>
  );
}
